import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import SiaeSection from './admin/SiaeSection';
import { CapodannoMenuEditor } from './admin/CapodannoMenuEditor';
import { CapodannoIncludedEditor } from './admin/CapodannoIncludedEditor';

import {
  LayoutDashboard, Calendar, Users, LogOut, Plus, Trash2,
  Edit3, X, Check, ChevronDown, ChevronUp, BarChart2, Eye, EyeOff,
  Upload, ImageIcon, MapPin, Menu, DollarSign, Armchair, Download,
  Zap, Clock, Trophy, Anchor, Phone, FileText
} from 'lucide-react';
import TableMap from './TableMap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Login Screen ─────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPwd, setShowPwd]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', res.data.token);
      onLogin(res.data.token);
    } catch {
      setError('Password errata. Riprova.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
      <div className="w-full max-w-md px-4">
        <div className="confirmation-card">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_3a008eb2-6f58-4bf6-b16e-78d189a6aa15/artifacts/lymh6mbi_LA%20PESCHIERA%20CLUB%20WHITE%20E%20STREET.png"
              alt="La Peschiera Club"
              className="h-16 object-contain mx-auto mb-4"
            />
            <h1 className="font-playfair text-2xl font-bold text-white">Pannello Admin</h1>
            <p className="text-gray-400 font-manrope mt-1">Accesso riservato</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <input
                data-testid="admin-password-input"
                type={showPwd ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="input-elegant pr-12"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {error && <p data-testid="admin-login-error" className="text-red-400 text-sm font-manrope">{error}</p>}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center"
            >
              {loading ? 'Accesso...' : 'Accedi'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <a href="/" className="text-gray-500 hover:text-gold text-sm font-manrope transition-colors">
              ← Torna al sito
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Stats Card ───────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color = 'gold' }) {
  return (
    <div className="card-dark p-6 flex items-center gap-4">
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: color === 'aqua' ? 'rgba(79,195,212,0.1)' : 'rgba(201,168,76,0.1)' }}
      >
        <span style={{ color: color === 'aqua' ? 'var(--color-aqua)' : 'var(--color-gold)' }}>{icon}</span>
      </div>
      <div>
        <p className="text-gray-400 font-manrope text-sm">{label}</p>
        <p className="font-playfair text-3xl font-bold text-white">{value}</p>
      </div>
    </div>
  );
}

// ── Table Assign Field ───────────────────────────────────────────────────────
function TableAssignField({ booking, token, onSaved }) {
  const [value, setValue] = useState(booking.table_number ?? '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const save = async () => {
    const num = value === '' ? null : parseInt(value);
    if (num !== null && (num < 1 || num > 150)) {
      alert('Il numero tavolo deve essere tra 1 e 150');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/bookings/${booking.id}/table`,
        { table_number: num },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore nell\'assegnazione');
    }
    setSaving(false);
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
      <MapPin size={16} className="text-gold shrink-0" />
      <span className="text-gray-300 font-manrope text-sm font-600 shrink-0">Numero Tavolo:</span>
      <input
        type="number"
        min="1"
        max="150"
        placeholder="1-150"
        value={value}
        onChange={e => setValue(e.target.value)}
        className="input-elegant"
        style={{ width: '90px', height: '36px', fontSize: '0.85rem' }}
        data-testid={`table-assign-input-${booking.id}`}
      />
      <button
        onClick={save}
        disabled={saving}
        className="btn-primary text-xs py-1.5 px-4"
        style={{ height: '36px', fontSize: '0.8rem' }}
        data-testid={`table-assign-save-${booking.id}`}
      >
        {saving ? '...' : success ? 'Salvato!' : 'Assegna'}
      </button>
      {booking.table_number && (
        <span className="text-gold font-manrope text-xs ml-1">
          (attuale: {booking.table_number})
        </span>
      )}
    </div>
  );
}

// ── Image Upload Field ────────────────────────────────────────────────────────
function ImageUploadField({ value, onChange, token }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]     = useState(value || '');
  const [error, setError]         = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data.url);
      onChange(res.data.url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore nel caricamento.');
    } finally { setUploading(false); }
  };

  const handleUrlChange = (e) => {
    setPreview(e.target.value);
    onChange(e.target.value);
  };

  return (
    <div>
      <label className="block text-sm font-manrope font-600 text-gray-300 mb-2">
        Foto evento
      </label>

      {/* Preview */}
      {preview && (
        <div className="mb-3 relative rounded-xl overflow-hidden" style={{ height: '140px' }}>
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={() => setPreview('')}
          />
          <button
            type="button"
            onClick={() => { setPreview(''); onChange(''); }}
            className="absolute top-2 right-2 bg-black/70 rounded-full p-1 text-white hover:text-red-400 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Upload button */}
      <div className="flex gap-2">
        <button
          type="button"
          data-testid="upload-image-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-secondary flex items-center gap-2 text-sm py-2.5 px-4 shrink-0"
          style={{ minHeight: '48px', fontSize: '0.9rem' }}
        >
          {uploading ? (
            <><Upload size={16} className="animate-bounce" /> Caricamento...</>
          ) : (
            <><Upload size={16} /> Carica dal PC</>
          )}
        </button>
        <input
          type="text"
          placeholder="oppure incolla URL immagine..."
          value={preview}
          onChange={handleUrlChange}
          className="input-elegant flex-1"
          style={{ height: '48px', fontSize: '0.9rem' }}
        />
      </div>

      {error && <p className="text-red-400 text-xs mt-1 font-manrope">{error}</p>}
      <p className="text-gray-600 text-xs mt-1 font-manrope">JPG, PNG, WEBP · max 5MB</p>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}


function EventModal({ event, token, defaultClub, onClose, onSaved }) {
  const isEdit = !!event;
  const [form, setForm] = useState({
    title: event?.title || '',
    date: event?.date || '',
    time: event?.time || '21:00',
    description: event?.description || '',
    image_url: event?.image_url || '',
    dj: event?.dj || '',
    special_guest: event?.special_guest || '',
    music_genre: event?.music_genre || 'Liscio & Balli di Gruppo',
    club: event?.club || defaultClub || 'peschiera',
    bookings_disabled: event?.bookings_disabled || false,
    show_list_counter: event?.show_list_counter || false,
    age_restriction: event?.age_restriction || '',
    dress_code: event?.dress_code || '',
    packages: event?.packages || [
      { type: 'solo_ingresso', label: 'Solo Ingresso', price: 10, icon: '🎟️' },
      { type: 'ingresso_guardaroba', label: 'Ingresso + Guardaroba', price: 15, icon: '🧥' },
      { type: 'ingresso_solo_tavolo', label: 'Ingresso solo Tavolo', price: 12, icon: '🪑' },
      { type: 'ingresso_tavolo_bottiglia', label: 'Ingresso + Tavolo e Bottiglia', price: 40, icon: '🍾' },
      { type: 'ingresso_tavolo_bottiglia_guardaroba', label: 'Ingresso + Tavolo, Bottiglia e Guardaroba', price: 50, icon: '⭐' },
    ],
    tickets_low: event?.tickets_low || false,
    sold_out: event?.sold_out || false,
    disabled_seats: event?.disabled_seats || [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const handleSave = async () => {
    setLoading(true); setError('');
    const payload = {
      ...form,
      packages: form.packages.map(p => (Array.isArray(p.included)
        ? { ...p, included: p.included.map(s => s.trim()).filter(Boolean) } : p)),
    };
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/events/${event.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/events`, payload, { headers });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante il salvataggio.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="min-h-full flex items-start justify-center py-8">
      <div className="confirmation-card w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-xl font-bold text-white">
            {isEdit ? 'Modifica evento' : 'Nuovo evento'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={22} /></button>
        </div>
        <div className="space-y-4">
          {[
            { key: 'title', label: 'Titolo *', placeholder: 'Nome serata' },
            { key: 'date', label: 'Data *', placeholder: 'YYYY-MM-DD', type: 'date' },
            { key: 'time', label: 'Orario *', placeholder: '21:00', type: 'time' },
            { key: 'dj', label: 'DJ / Orchestra', placeholder: 'Nome artista' },
            { key: 'special_guest', label: 'Special Guest', placeholder: 'Nome ospite speciale (opzionale)' },
            { key: 'music_genre', label: 'Genere musicale', placeholder: 'Liscio & Balli di Gruppo' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">{f.label}</label>
              <input
                type={f.type || 'text'}
                placeholder={f.placeholder}
                value={form[f.key]}
                onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))}
                className="input-elegant"
              />
            </div>
          ))}
          <ImageUploadField
            value={form.image_url}
            onChange={url => setForm(v => ({ ...v, image_url: url }))}
            token={token}
          />
          {/* Club selector */}
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-2">Club *</label>
            <div className="grid grid-cols-3 gap-2" data-testid="event-club-selector">
              <button
                type="button"
                onClick={() => setForm(v => ({ ...v, club: 'peschiera' }))}
                data-testid="event-club-peschiera"
                className={`px-3 py-3 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center justify-center gap-1.5 ${
                  form.club === 'peschiera' ? 'text-white' : 'text-gray-400'
                }`}
                style={form.club === 'peschiera' ? {
                  background: 'linear-gradient(135deg, rgba(201,168,76,0.25), rgba(201,168,76,0.12))',
                  border: '1px solid rgba(201,168,76,0.5)',
                } : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                ◆ Peschiera
              </button>
              <button
                type="button"
                onClick={() => setForm(v => ({ ...v, club: 'jimmys' }))}
                data-testid="event-club-jimmys"
                className={`px-3 py-3 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center justify-center gap-1.5 ${
                  form.club === 'jimmys' ? 'text-white' : 'text-gray-400'
                }`}
                style={form.club === 'jimmys' ? {
                  background: 'linear-gradient(135deg, rgba(236,72,153,0.3), rgba(168,85,247,0.22))',
                  border: '1px solid rgba(236,72,153,0.55)',
                } : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                ★ Jimmy's
              </button>
              <button
                type="button"
                onClick={() => setForm(v => ({ ...v, club: 'capodanno' }))}
                data-testid="event-club-capodanno"
                className={`px-3 py-3 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center justify-center gap-1.5 ${
                  form.club === 'capodanno' ? 'text-white' : 'text-gray-400'
                }`}
                style={form.club === 'capodanno' ? {
                  background: 'linear-gradient(135deg, rgba(251,191,36,0.3), rgba(239,68,68,0.22))',
                  border: '1px solid rgba(251,191,36,0.55)',
                } : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                🎉 Capodanno
              </button>
            </div>
          </div>
          {/* Prezziario pacchetti */}
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-2">Pacchetti ingresso</label>
            <div className="grid grid-cols-1 gap-2">
              {form.packages.map((pkg, idx) => (
                <div key={pkg.type + idx} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: pkg.enabled === false ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', opacity: pkg.enabled === false ? 0.4 : 1, transition: 'opacity 0.2s' }}>
                  <input
                    type="checkbox"
                    checked={pkg.enabled !== false}
                    onChange={() => {
                      const newPkgs = [...form.packages];
                      newPkgs[idx] = { ...newPkgs[idx], enabled: newPkgs[idx].enabled === false ? true : false };
                      setForm(v => ({ ...v, packages: newPkgs }));
                    }}
                    className="w-4 h-4 shrink-0" style={{ accentColor: '#c9a84c' }}
                    data-testid={`pkg-toggle-${idx}`}
                  />
                  <span className="text-xl shrink-0">{pkg.icon}</span>
                  {pkg.custom ? (
                    <input
                      type="text"
                      value={pkg.label}
                      onChange={e => {
                        const newPkgs = [...form.packages];
                        newPkgs[idx] = { ...newPkgs[idx], label: e.target.value };
                        setForm(v => ({ ...v, packages: newPkgs }));
                      }}
                      className="input-elegant text-sm flex-1"
                      style={{ height: '34px' }}
                      placeholder="Nome pacchetto"
                      data-testid={`pkg-label-${idx}`}
                    />
                  ) : (
                    <span className="text-gray-300 font-manrope text-sm flex-1 truncate">{pkg.label}</span>
                  )}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-gray-500 text-sm">&euro;</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={pkg.price}
                      data-testid={`pkg-price-${pkg.type || idx}`}
                      onChange={e => {
                        const newPkgs = [...form.packages];
                        newPkgs[idx] = { ...newPkgs[idx], price: parseFloat(e.target.value) || 0 };
                        setForm(v => ({ ...v, packages: newPkgs }));
                      }}
                      className="input-elegant text-right"
                      style={{ width: '84px', height: '34px', fontSize: '0.9rem', paddingRight: '8px' }}
                    />
                  </div>
                  {pkg.custom && (
                    <button
                      onClick={() => setForm(v => ({ ...v, packages: v.packages.filter((_, i) => i !== idx) }))}
                      className="text-gray-600 hover:text-red-400 transition-colors shrink-0"
                      data-testid={`pkg-remove-${idx}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setForm(v => ({
                ...v,
                packages: [...v.packages, { type: `custom_${Date.now()}`, label: '', price: 0, icon: '🎫', custom: true, enabled: true }]
              }))}
              className="mt-2 flex items-center gap-2 text-sm font-manrope font-600 text-gold hover:text-gold-hover transition-colors py-2"
              data-testid="add-custom-package"
            >
              <Plus size={15} /> Aggiungi pacchetto personalizzato
            </button>
          </div>
          {form.club === 'capodanno' && (
            <CapodannoIncludedEditor
              packages={form.packages}
              onChange={(type, included) => setForm(v => ({
                ...v,
                packages: v.packages.map(p => (p.type === type ? { ...p, included } : p)),
              }))}
            />
          )}
          {form.club === 'capodanno' && form.packages.some(p => p.type === 'capodanno_cena') && (
            <CapodannoMenuEditor
              menu={form.packages.find(p => p.type === 'capodanno_cena').menu}
              onChange={menu => setForm(v => ({
                ...v,
                packages: v.packages.map(p => (p.type === 'capodanno_cena' ? { ...p, menu } : p)),
              }))}
            />
          )}
          {/* Biglietti in esaurimento */}
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <label className="flex items-center gap-3 cursor-pointer flex-1">
              <input type="checkbox" checked={form.tickets_low}
                data-testid="toggle-tickets-low"
                onChange={e => setForm(v => ({ ...v, tickets_low: e.target.checked }))}
                className="w-5 h-5" style={{ accentColor: '#f87171' }} />
              <span className="font-manrope font-600 text-sm" style={{ color: form.tickets_low ? '#f87171' : '#9ca3af' }}>
                🔥 Biglietti in esaurimento
              </span>
            </label>
          </div>
          {/* Sold Out */}
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: form.sold_out ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${form.sold_out ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
            <label className="flex items-center gap-3 cursor-pointer flex-1">
              <input type="checkbox" checked={form.sold_out}
                data-testid="toggle-sold-out"
                onChange={e => setForm(v => ({ ...v, sold_out: e.target.checked, tickets_low: e.target.checked ? false : v.tickets_low }))}
                className="w-5 h-5" style={{ accentColor: '#ef4444' }} />
              <span className="font-manrope font-600 text-sm" style={{ color: form.sold_out ? '#ef4444' : '#9ca3af' }}>
                🚫 SOLD OUT — Prenotazioni chiuse
              </span>
            </label>
          </div>
          {/* Bookings disabled (view-only) */}
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: form.bookings_disabled ? 'rgba(168,85,247,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${form.bookings_disabled ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
            <label className="flex items-center gap-3 cursor-pointer flex-1">
              <input type="checkbox" checked={form.bookings_disabled}
                data-testid="toggle-bookings-disabled"
                onChange={e => setForm(v => ({ ...v, bookings_disabled: e.target.checked }))}
                className="w-5 h-5" style={{ accentColor: '#a855f7' }} />
              <span className="font-manrope font-600 text-sm" style={{ color: form.bookings_disabled ? '#a855f7' : '#9ca3af' }}>
                👁 Solo visualizzazione — nascondi tasto prenotazione
              </span>
            </label>
          </div>
          {/* Live list counter toggle */}
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: form.show_list_counter ? 'rgba(236,72,153,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${form.show_list_counter ? 'rgba(236,72,153,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
            <label className="flex items-center gap-3 cursor-pointer flex-1">
              <input type="checkbox" checked={form.show_list_counter}
                data-testid="toggle-list-counter"
                onChange={e => setForm(v => ({ ...v, show_list_counter: e.target.checked }))}
                className="w-5 h-5" style={{ accentColor: '#ec4899' }} />
              <span className="font-manrope font-600 text-sm" style={{ color: form.show_list_counter ? '#ec4899' : '#9ca3af' }}>
                🔥 Mostra contatore live "X persone già in lista" sul sito
              </span>
            </label>
          </div>
          {/* Age + Dress code */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">Età minima</label>
              <input type="text" placeholder="Es. +17 o lascia vuoto"
                value={form.age_restriction}
                data-testid="input-age-restriction"
                onChange={e => setForm(v => ({ ...v, age_restriction: e.target.value }))}
                className="input-elegant" />
            </div>
            <div>
              <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">Dress code</label>
              <input type="text" placeholder="Es. Smart Look · No sneakers"
                value={form.dress_code}
                data-testid="input-dress-code"
                onChange={e => setForm(v => ({ ...v, dress_code: e.target.value }))}
                className="input-elegant" />
            </div>
          </div>
          {/* Disponibilità posti */}
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-2">Posti non disponibili (esauriti)</label>
            <div className="grid grid-cols-1 gap-2">
              {[
                { id: 'laterale_destra', label: 'Laterale a destra' },
                { id: 'laterale_sinistra', label: 'Laterale a sinistra' },
                { id: 'fronte_orchestra', label: 'Fronte orchestra' },
              ].map(pos => (
                <label key={pos.id} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <input type="checkbox"
                    data-testid={`disable-seat-${pos.id}`}
                    checked={form.disabled_seats.includes(pos.id)}
                    onChange={e => {
                      const seats = e.target.checked
                        ? [...form.disabled_seats, pos.id]
                        : form.disabled_seats.filter(s => s !== pos.id);
                      setForm(v => ({ ...v, disabled_seats: seats }));
                    }}
                    className="w-5 h-5" style={{ accentColor: '#f87171' }} />
                  <span className="font-manrope text-sm text-gray-300">{pos.label}</span>
                  {form.disabled_seats.includes(pos.id) && <span className="text-red-400 text-xs ml-auto">ESAURITO</span>}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">Descrizione *</label>
            <textarea
              placeholder="Descrizione della serata..."
              value={form.description}
              onChange={e => setForm(v => ({ ...v, description: e.target.value }))}
              className="input-elegant"
              style={{ height: '100px', resize: 'vertical' }}
            />
          </div>
        </div>
        {error && <p className="text-red-400 text-sm font-manrope mt-3">{error}</p>}
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1 justify-center">Annulla</button>
          <button
            data-testid="save-event-btn"
            onClick={handleSave}
            disabled={loading || !form.title || !form.date}
            className="btn-primary flex-1 justify-center"
          >
            {loading ? 'Salvataggio...' : <><Check size={16} /> Salva</>}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

// ── Special Event Modal ──────────────────────────────────────────────────────
function SpecialEventModal({ event, token, onClose, onSaved }) {
  const isEdit = !!event;
  const [form, setForm] = useState({
    title: event?.title || '',
    subtitle: event?.subtitle || '',
    date_range: event?.date_range || '',
    image_url: event?.image_url || '',
    description: event?.description || '',
    cta_label: event?.cta_label || 'PRENOTA ORA',
    bookings_disabled: event?.bookings_disabled || false,
    sold_out: event?.sold_out || false,
    sort_order: event?.sort_order || 0,
    included_items: event?.included_items || [],
  });
  const [newItem, setNewItem] = useState({ icon: '', label: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/special-events/${event.id}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/special-events`, form, { headers });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore durante il salvataggio.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="min-h-full flex items-start justify-center py-8">
      <div className="confirmation-card w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-playfair text-xl font-bold text-white flex items-center gap-2">
            <Anchor size={18} style={{ color: '#fbbf24' }} />
            {isEdit ? 'Modifica evento speciale' : 'Nuovo evento speciale'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={22} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">Titolo *</label>
            <input type="text" placeholder="Es. La Crociera che non dimenticherai mai!"
              value={form.title} onChange={e => setForm(v => ({ ...v, title: e.target.value }))}
              className="input-elegant" data-testid="special-input-title" />
          </div>
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">Sottotitolo</label>
            <input type="text" placeholder="Es. 7 giorni / 6 notti — Costa Diadema"
              value={form.subtitle} onChange={e => setForm(v => ({ ...v, subtitle: e.target.value }))}
              className="input-elegant" />
          </div>
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">Date *</label>
            <input type="text" placeholder="Es. 21–27 Marzo 2027"
              value={form.date_range} onChange={e => setForm(v => ({ ...v, date_range: e.target.value }))}
              className="input-elegant" data-testid="special-input-date" />
          </div>
          <ImageUploadField
            value={form.image_url}
            onChange={url => setForm(v => ({ ...v, image_url: url }))}
            token={token}
          />
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">Descrizione</label>
            <textarea placeholder="Descrizione dettagliata dell'evento..."
              value={form.description} onChange={e => setForm(v => ({ ...v, description: e.target.value }))}
              className="input-elegant" style={{ height: '120px', resize: 'vertical' }} />
          </div>
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-1">Etichetta bottone</label>
            <input type="text" placeholder="PRENOTA ORA"
              value={form.cta_label} onChange={e => setForm(v => ({ ...v, cta_label: e.target.value }))}
              className="input-elegant" />
          </div>
          {/* Included items editor */}
          <div>
            <label className="block text-sm font-manrope font-600 text-gray-300 mb-2">
              Cosa è incluso (servizi/feature in evidenza)
            </label>
            <div className="space-y-2 mb-2" data-testid="included-items-list">
              {(form.included_items || []).map((it, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)' }}>
                  <input
                    type="text"
                    value={it.icon || ''}
                    onChange={e => {
                      const items = [...form.included_items];
                      items[idx] = { ...items[idx], icon: e.target.value };
                      setForm(v => ({ ...v, included_items: items }));
                    }}
                    placeholder="🛏"
                    className="w-14 px-2 py-2 rounded-md text-center text-xl bg-black/30 text-white outline-none"
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                  />
                  <input
                    type="text"
                    value={it.label || ''}
                    onChange={e => {
                      const items = [...form.included_items];
                      items[idx] = { ...items[idx], label: e.target.value };
                      setForm(v => ({ ...v, included_items: items }));
                    }}
                    placeholder="Es. Pensione completa"
                    className="flex-1 px-3 py-2 rounded-md bg-black/30 text-white outline-none font-manrope text-sm"
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                  />
                  <button
                    type="button"
                    onClick={() => setForm(v => ({ ...v, included_items: v.included_items.filter((_, i) => i !== idx) }))}
                    data-testid={`included-item-remove-${idx}`}
                    className="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newItem.icon}
                onChange={e => setNewItem(v => ({ ...v, icon: e.target.value }))}
                placeholder="🎟"
                className="w-14 px-2 py-2 rounded-md text-center text-xl bg-black/30 text-white outline-none"
                style={{ border: '1px solid rgba(255,255,255,0.08)' }}
              />
              <input
                type="text"
                value={newItem.label}
                onChange={e => setNewItem(v => ({ ...v, label: e.target.value }))}
                placeholder="Aggiungi servizio..."
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (newItem.label.trim()) {
                      setForm(v => ({ ...v, included_items: [...(v.included_items || []), { icon: newItem.icon || '✓', label: newItem.label.trim() }] }));
                      setNewItem({ icon: '', label: '' });
                    }
                  }
                }}
                className="flex-1 px-3 py-2 rounded-md bg-black/30 text-white outline-none font-manrope text-sm"
                style={{ border: '1px solid rgba(255,255,255,0.08)' }}
              />
              <button
                type="button"
                data-testid="included-item-add"
                onClick={() => {
                  if (newItem.label.trim()) {
                    setForm(v => ({ ...v, included_items: [...(v.included_items || []), { icon: newItem.icon || '✓', label: newItem.label.trim() }] }));
                    setNewItem({ icon: '', label: '' });
                  }
                }}
                className="w-8 h-8 rounded-md flex items-center justify-center transition-colors"
                style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}
              >
                <Plus size={14} />
              </button>
            </div>
            <p className="text-gray-500 font-manrope text-xs mt-2">
              Usa un'emoji come icona (es. 🛏 🍹 🏊 🎭 🎧 🎟). Clicca Enter o + per aggiungere.
            </p>
          </div>
          {/* Sold Out */}
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: form.sold_out ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${form.sold_out ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
            <label className="flex items-center gap-3 cursor-pointer flex-1">
              <input type="checkbox" checked={form.sold_out}
                data-testid="special-toggle-soldout"
                onChange={e => setForm(v => ({ ...v, sold_out: e.target.checked }))}
                className="w-5 h-5" style={{ accentColor: '#ef4444' }} />
              <span className="font-manrope font-600 text-sm" style={{ color: form.sold_out ? '#ef4444' : '#9ca3af' }}>
                🚫 SOLD OUT — Prenotazioni chiuse
              </span>
            </label>
          </div>
          {/* Bookings disabled */}
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: form.bookings_disabled ? 'rgba(6,182,212,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${form.bookings_disabled ? 'rgba(6,182,212,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
            <label className="flex items-center gap-3 cursor-pointer flex-1">
              <input type="checkbox" checked={form.bookings_disabled}
                data-testid="special-toggle-view-only"
                onChange={e => setForm(v => ({ ...v, bookings_disabled: e.target.checked }))}
                className="w-5 h-5" style={{ accentColor: '#22d3ee' }} />
              <span className="font-manrope font-600 text-sm" style={{ color: form.bookings_disabled ? '#22d3ee' : '#9ca3af' }}>
                👁 Solo visualizzazione — nascondi bottone prenotazione
              </span>
            </label>
          </div>
        </div>
        {error && <p className="text-red-400 text-sm font-manrope mt-3">{error}</p>}
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-secondary flex-1 justify-center">Annulla</button>
          <button
            data-testid="save-special-event-btn"
            onClick={handleSave}
            disabled={loading || !form.title || !form.date_range}
            className="btn-primary flex-1 justify-center"
          >
            {loading ? 'Salvataggio...' : <><Check size={16} /> Salva</>}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

// ── Special Events Admin Section ─────────────────────────────────────────────
function SpecialEventsSection({ token }) {
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [filterEvent, setFilterEvent] = useState('');
  const [expanded, setExpanded] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const loadEvents = useCallback(async () => {
    try { const r = await axios.get(`${API}/special-events`); setEvents(r.data); } catch {}
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      const params = filterEvent ? { special_event_id: filterEvent } : {};
      const r = await axios.get(`${API}/admin/special-bookings`, { headers, params });
      setBookings(r.data);
    } catch {}
  }, [token, filterEvent]);

  useEffect(() => { loadEvents(); loadBookings(); }, [filterEvent]);

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Vuoi davvero rimuovere questo evento speciale?')) return;
    await axios.delete(`${API}/admin/special-events/${id}`, { headers });
    loadEvents();
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm('Eliminare questa richiesta?')) return;
    await axios.delete(`${API}/admin/special-bookings/${id}`, { headers });
    loadBookings();
  };

  const toggleContacted = async (b) => {
    await axios.put(`${API}/admin/special-bookings/${b.id}`, { contacted: !b.contacted }, { headers });
    loadBookings();
  };

  const totalPeople = bookings.reduce((s, b) => s + (b.num_people || 0), 0);

  return (
    <div data-testid="admin-special-section">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <div className="card-dark p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(251,191,36,0.12)' }}>
            <Anchor size={22} style={{ color: '#fbbf24' }} />
          </div>
          <div>
            <p className="text-gray-400 font-manrope text-sm">Eventi attivi</p>
            <p className="font-playfair text-3xl font-bold text-white" data-testid="special-stat-events">{events.length}</p>
          </div>
        </div>
        <div className="card-dark p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(251,191,36,0.12)' }}>
            <Phone size={22} style={{ color: '#fbbf24' }} />
          </div>
          <div>
            <p className="text-gray-400 font-manrope text-sm">Richieste totali</p>
            <p className="font-playfair text-3xl font-bold text-white" data-testid="special-stat-bookings">{bookings.length}</p>
          </div>
        </div>
        <div className="card-dark p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(251,191,36,0.12)' }}>
            <Users size={22} style={{ color: '#fbbf24' }} />
          </div>
          <div>
            <p className="text-gray-400 font-manrope text-sm">Persone attese</p>
            <p className="font-playfair text-3xl font-bold text-white" data-testid="special-stat-people">{totalPeople}</p>
          </div>
        </div>
      </div>

      {/* Events list */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-playfair text-xl font-bold text-white flex items-center gap-2">
            <Anchor size={18} style={{ color: '#fbbf24' }} />
            Eventi speciali
          </h2>
          <button
            onClick={() => { setEditEvent(null); setShowModal(true); }}
            data-testid="add-special-event-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full font-manrope font-700 text-sm transition-all"
            style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#0a1929' }}
          >
            <Plus size={16} /> Nuovo evento
          </button>
        </div>
        <div className="space-y-3">
          {events.length === 0 ? (
            <div className="card-dark p-10 text-center text-gray-400 font-manrope">Nessun evento speciale attivo.</div>
          ) : events.map(ev => (
            <div key={ev.id} data-testid={`special-event-row-${ev.id}`} className="card-dark p-4 flex items-center gap-4">
              {ev.image_url && (
                <img src={ev.image_url} alt={ev.title} className="w-16 h-16 rounded-lg object-cover shrink-0" style={{ background: '#0a1929' }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-playfair font-bold text-white text-base leading-tight">{ev.title}</p>
                  {ev.sold_out && <span className="text-xs font-manrope font-700 px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>SOLD OUT</span>}
                  {ev.bookings_disabled && <span className="text-xs font-manrope font-700 px-2 py-0.5 rounded-full" style={{ background: 'rgba(6,182,212,0.12)', color: '#22d3ee' }}>SOLO ANNUNCIO</span>}
                </div>
                <p className="font-manrope text-sm mt-0.5" style={{ color: '#fbbf24' }}>{ev.date_range}</p>
                {ev.subtitle && <p className="text-gray-500 text-xs mt-0.5 truncate">{ev.subtitle}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => { setEditEvent(ev); setShowModal(true); }}
                  data-testid={`edit-special-event-${ev.id}`}
                  className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-gray-400 hover:border-amber-400 hover:text-amber-400 transition-all"
                >
                  <Edit3 size={16} />
                </button>
                <button
                  onClick={() => handleDeleteEvent(ev.id)}
                  data-testid={`delete-special-event-${ev.id}`}
                  className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-gray-400 hover:border-red-400 hover:text-red-400 transition-all"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bookings list */}
      <div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
          <h2 className="font-playfair text-xl font-bold text-white flex items-center gap-2">
            <Phone size={18} style={{ color: '#fbbf24' }} />
            Richieste prenotazione
          </h2>
          <select
            value={filterEvent}
            onChange={e => setFilterEvent(e.target.value)}
            className="input-elegant w-full sm:w-auto"
            style={{ minWidth: '220px' }}
            data-testid="special-bookings-filter"
          >
            <option value="">Tutti gli eventi</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.title}</option>
            ))}
          </select>
        </div>
        {bookings.length === 0 ? (
          <div className="card-dark p-10 text-center text-gray-400 font-manrope">Nessuna richiesta ricevuta.</div>
        ) : (
          <div className="space-y-2">
            {bookings.map(b => (
              <div key={b.id} data-testid={`special-booking-row-${b.id}`} className="card-dark overflow-hidden">
                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/3 transition-colors" onClick={() => setExpanded(expanded === b.id ? null : b.id)}>
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleContacted(b); }}
                      data-testid={`special-toggle-contacted-${b.id}`}
                      className="w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all shrink-0"
                      style={{
                        borderColor: b.contacted ? '#22c55e' : 'rgba(255,255,255,0.2)',
                        background: b.contacted ? 'rgba(34,197,94,0.15)' : 'transparent',
                      }}
                      title={b.contacted ? 'Ricontattato' : 'Da ricontattare'}
                    >
                      {b.contacted && <Check size={14} style={{ color: '#22c55e' }} strokeWidth={3} />}
                    </button>
                    <span className="font-playfair font-bold text-xs sm:text-sm shrink-0" style={{ color: '#fbbf24' }}>{b.booking_code}</span>
                    <span className="text-white font-manrope font-600 text-sm truncate">{b.first_name || '(senza nome)'}</span>
                    <a href={`tel:${b.phone}`} onClick={e => e.stopPropagation()} className="font-manrope text-sm hover:underline" style={{ color: '#fbbf24' }}>{b.phone}</a>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-gray-400 font-manrope text-sm">{b.num_people} pers.</span>
                    <button
                      onClick={e => { e.stopPropagation(); handleDeleteBooking(b.id); }}
                      className="text-gray-600 hover:text-red-400 transition-colors p-1"
                      data-testid={`special-delete-booking-${b.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                    {expanded === b.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </div>
                {expanded === b.id && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <p className="text-gray-500 text-xs font-manrope uppercase tracking-wider mb-0.5">Evento</p>
                        <p className="text-white font-manrope text-sm">{b.special_event_title}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs font-manrope uppercase tracking-wider mb-0.5">Ricevuto il</p>
                        <p className="text-white font-manrope text-sm">{new Date(b.created_at).toLocaleString('it-IT')}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs font-manrope uppercase tracking-wider mb-0.5">Stato</p>
                        <p className="font-manrope text-sm font-600" style={{ color: b.contacted ? '#22c55e' : '#fbbf24' }}>
                          {b.contacted ? '✓ Ricontattato' : '⏳ Da ricontattare'}
                        </p>
                      </div>
                      {b.notes && (
                        <div className="col-span-full">
                          <p className="text-gray-500 text-xs font-manrope uppercase tracking-wider mb-0.5">Note</p>
                          <p className="text-gray-300 font-manrope text-sm italic">{b.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <SpecialEventModal
          event={editEvent}
          token={token}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadEvents(); }}
        />
      )}
    </div>
  );
}

// ── Main Admin Dashboard ──────────────────────────────────────────────────────
export default function AdminPage() {
  const [token, setToken]         = useState(() => localStorage.getItem('admin_token'));
  const [tab, setTab]             = useState('dashboard');
  const [activeClub, setActiveClub] = useState(() => localStorage.getItem('admin_active_club') || 'peschiera');
  const [stats, setStats]         = useState(null);
  const [bookings, setBookings]   = useState([]);
  const [events, setEvents]       = useState([]);
  const [filterEvent, setFilterEvent] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadStats    = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/stats`, { headers, params: { club: activeClub } }); setStats(r.data); } catch {}
  }, [token, activeClub]);

  const loadBookings = useCallback(async () => {
    try {
      const params = filterEvent ? { event_id: filterEvent } : { club: activeClub };
      const r = await axios.get(`${API}/admin/bookings`, { headers, params });
      setBookings(r.data);
    } catch {}
  }, [token, filterEvent, activeClub]);

  const loadEvents   = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/events`, { params: { club: activeClub } });
      setEvents(r.data);
    } catch {}
  }, [activeClub]);

  useEffect(() => {
    if (!token) return;
    loadStats(); loadEvents();
    setFilterEvent('');
  }, [token, activeClub]);

  useEffect(() => {
    if (tab === 'bookings') loadBookings();
  }, [tab, filterEvent, activeClub]);

  const handleClubChange = (club) => {
    setActiveClub(club);
    localStorage.setItem('admin_active_club', club);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Vuoi davvero rimuovere questo evento?')) return;
    await axios.delete(`${API}/admin/events/${id}`, { headers });
    loadEvents(); loadStats();
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm('Eliminare questa prenotazione?')) return;
    await axios.delete(`${API}/admin/bookings/${id}`, { headers });
    loadBookings(); loadStats();
  };

  const exportEventPDF = async (ev) => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers, params: { event_id: ev.id } });
      const evBookings = res.data || [];

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFillColor(15, 15, 25);
      doc.rect(0, 0, 297, 210, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(201, 168, 76);
      doc.text(`La Peschiera Club`, 14, 15);

      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text(ev.title, 14, 23);

      doc.setFontSize(9);
      doc.setTextColor(160, 160, 170);
      const dateStr = new Date(ev.date + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`${dateStr} - ore ${ev.time || '22:00'}`, 14, 29);

      doc.setTextColor(201, 168, 76);
      const totalRevenue = evBookings.reduce((s, b) => s + ((b.package_price || 0) * (b.num_people || 1)) + (b.extras?.apericena ? 20 * (b.num_people || 1) : 0), 0);
      doc.text(`Totale prenotazioni: ${evBookings.length}  |  Persone attese: ${evBookings.reduce((s, b) => s + (b.num_people || 0), 0)}  |  Incasso previsto: € ${totalRevenue.toFixed(0)}`, 14, 35);

      const rows = evBookings.map(b => {
        const extras = [];
        if (b.extras?.torta) extras.push(b.extras.torta_testo ? `Torta: "${b.extras.torta_testo}"` : 'Torta');
        if (b.extras?.bottiglia) extras.push(b.extras.bottiglia_nome ? `Bott: ${b.extras.bottiglia_nome}` : 'Bottiglia');
        if (b.extras?.apericena) extras.push('Apericena');

        const price = (b.package_price || 0) * (b.num_people || 1);
        const apericena = b.extras?.apericena ? 20 * (b.num_people || 1) : 0;

        return [
          b.booking_code || '',
          `${b.first_name} ${b.last_name}`,
          b.phone || '-',
          String(b.num_people || 0),
          b.package_label || '-',
          b.table_number ? String(b.table_number) : '-',
          b.table_position || '-',
          extras.join(', ') || '-',
          `€ ${b.package_price || 0}`,
          b.notes || '-',
        ];
      });

      autoTable(doc, {
        startY: 40,
        head: [['Codice', 'Nominativo', 'Telefono', 'Pers.', 'Pacchetto', 'Tav.', 'Zona', 'Extra', '€/pers.', 'Note']],
        body: rows.length > 0 ? rows : [['', 'Nessuna prenotazione', '', '', '', '', '', '', '', '']],
        styles: { fontSize: 7, cellPadding: 2, textColor: [50, 50, 50] },
        headStyles: { fillColor: [30, 30, 50], textColor: [201, 168, 76], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 32 },
          3: { cellWidth: 12, halign: 'center' },
          5: { cellWidth: 12, halign: 'center' },
          6: { cellWidth: 20 },
          8: { cellWidth: 16, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: { fillColor: [245, 245, 250] },
        didDrawPage: (data) => {
          doc.setFontSize(7);
          doc.setTextColor(150, 150, 160);
          doc.text(`La Peschiera Club - ${ev.title} - Pagina ${data.pageNumber}`, 14, 205);
        },
      });

      doc.save(`Presenze_${ev.title.replace(/\s+/g, '_')}_${ev.date}.pdf`);
    } catch (err) {
      alert('Errore nell\'esportazione del PDF: ' + (err.message || err));
    }
  };

  const exportEventTablesPDF = async (ev) => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers, params: { event_id: ev.id } });
      const allBookings = res.data || [];
      // Only bookings with tavolo in package type
      const tableBookings = allBookings.filter(b => (b.package_type || '').includes('tavolo'));

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFillColor(15, 15, 25);
      doc.rect(0, 0, 297, 210, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(201, 168, 76);
      doc.text('La Peschiera Club — Tavoli', 14, 15);

      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text(ev.title, 14, 23);

      doc.setFontSize(9);
      doc.setTextColor(160, 160, 170);
      const dateStr = new Date(ev.date + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`${dateStr} - ore ${ev.time || '22:00'}`, 14, 29);

      const assigned = tableBookings.filter(b => b.table_number);
      const unassigned = tableBookings.filter(b => !b.table_number);
      doc.setTextColor(201, 168, 76);
      doc.text(`Tavoli assegnati: ${assigned.length}  |  Da assegnare: ${unassigned.length}  |  Persone tavolo: ${tableBookings.reduce((s, b) => s + (b.num_people || 0), 0)}`, 14, 35);

      const rows = tableBookings
        .sort((a, b) => (a.table_number || 999) - (b.table_number || 999))
        .map(b => {
          const extras = [];
          if (b.extras?.torta) extras.push(b.extras.torta_testo ? `Torta: "${b.extras.torta_testo}"` : 'Torta: Si');
          if (b.extras?.bottiglia) extras.push(b.extras.bottiglia_nome ? `Bottiglia: ${b.extras.bottiglia_nome}` : 'Bottiglia: Si');
          if (b.extras?.apericena) extras.push('Apericena');
          return [
            b.table_number ? String(b.table_number) : 'DA ASSEGNARE',
            `${b.first_name} ${b.last_name}`,
            b.phone || '-',
            String(b.num_people || 0),
            b.package_label || '-',
            b.table_position || '-',
            extras.join(' | ') || '-',
            `€ ${b.package_price || 0}`,
            b.notes || '-',
          ];
        });

      autoTable(doc, {
        startY: 40,
        head: [['Tav.', 'Nominativo', 'Telefono', 'Pers.', 'Pacchetto', 'Zona', 'Extra (Torta/Bottiglia)', '€/pers.', 'Note']],
        body: rows.length > 0 ? rows : [['', 'Nessuna prenotazione tavolo', '', '', '', '', '', '', '']],
        styles: { fontSize: 7.5, cellPadding: 2, textColor: [50, 50, 50] },
        headStyles: { fillColor: [30, 30, 50], textColor: [201, 168, 76], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
          1: { cellWidth: 32 },
          3: { cellWidth: 12, halign: 'center' },
          6: { cellWidth: 48 },
          7: { cellWidth: 16, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: { fillColor: [245, 245, 250] },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 0 && data.cell.raw === 'DA ASSEGNARE') {
            doc.setTextColor(220, 50, 50);
          }
        },
        didDrawPage: (data) => {
          doc.setFontSize(7);
          doc.setTextColor(150, 150, 160);
          doc.text(`La Peschiera Club - Tavoli ${ev.title} - Pagina ${data.pageNumber}`, 14, 205);
        },
      });

      doc.save(`Tavoli_${ev.title.replace(/\s+/g, '_')}_${ev.date}.pdf`);
    } catch (err) {
      alert('Errore nell\'esportazione del PDF: ' + (err.message || err));
    }
  };

  if (!token) return <AdminLogin onLogin={t => { setToken(t); }} />;

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} /> },
    { id: 'bookings',  label: 'Prenotazioni', icon: <Users size={18} /> },
    { id: 'events',    label: 'Gestione eventi', icon: <Calendar size={18} /> },
    { id: 'tables',    label: 'Mappa Tavoli', icon: <MapPin size={18} /> },
    { id: 'siae',      label: 'Borderò SIAE', icon: <FileText size={18} /> },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ background: 'var(--bg-primary)' }} data-testid="admin-dashboard">

      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ background: '#070710' }}>
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_3a008eb2-6f58-4bf6-b16e-78d189a6aa15/artifacts/lymh6mbi_LA%20PESCHIERA%20CLUB%20WHITE%20E%20STREET.png"
            alt="La Peschiera Club"
            className="h-8 object-contain"
          />
          <span className="text-gray-500 font-manrope text-xs uppercase tracking-widest">Admin</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-gray-400 hover:text-white p-2"
          data-testid="admin-mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden border-b border-white/10 px-4 py-3 space-y-1" style={{ background: '#070710' }}>
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setTab(item.id); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-manrope font-600 text-sm transition-all ${
                tab === item.id
                  ? 'bg-gold/10 text-gold border border-gold/20'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {item.icon} {item.label}
            </button>
          ))}
          <div className="pt-2 mt-2 border-t border-white/10 flex items-center justify-between">
            <a href="/" className="text-gray-400 hover:text-gold text-sm font-manrope transition-colors">← Torna al sito</a>
            <button onClick={handleLogout} className="flex items-center gap-2 text-gray-400 hover:text-red-400 text-sm font-manrope transition-colors">
              <LogOut size={14} /> Disconnetti
            </button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-white/10 flex-col" style={{ background: '#070710' }}>
        <div className="p-6 border-b border-white/10">
          <img
            src="https://customer-assets.emergentagent.com/job_3a008eb2-6f58-4bf6-b16e-78d189a6aa15/artifacts/lymh6mbi_LA%20PESCHIERA%20CLUB%20WHITE%20E%20STREET.png"
            alt="La Peschiera Club"
            className="h-10 object-contain mb-2"
          />
          <p className="text-gray-500 font-manrope text-xs uppercase tracking-widest">Pannello Admin</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => (
            <button
              key={item.id}
              data-testid={`admin-nav-${item.id}`}
              onClick={() => setTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-manrope font-600 text-sm transition-all ${
                tab === item.id
                  ? 'bg-gold/10 text-gold border border-gold/20'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {item.icon} {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <a href="/" className="flex items-center gap-2 text-gray-400 hover:text-gold text-sm font-manrope mb-3 transition-colors">
            ← Torna al sito
          </a>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 text-gray-400 hover:text-red-400 text-sm font-manrope transition-colors"
          >
            <LogOut size={16} /> Disconnetti
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto p-4 md:p-8">

        {/* Club selector tabs — applies to ALL tabs */}
        <div className="mb-6 flex items-center gap-1 sm:gap-2 p-1.5 rounded-2xl w-fit flex-wrap" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="club-tabs">
          <button
            onClick={() => handleClubChange('peschiera')}
            data-testid="club-tab-peschiera"
            className={`px-4 sm:px-6 py-2.5 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center gap-2 ${
              activeClub === 'peschiera'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={activeClub === 'peschiera' ? {
              background: 'linear-gradient(135deg, rgba(201,168,76,0.25), rgba(201,168,76,0.15))',
              border: '1px solid rgba(201,168,76,0.5)',
              boxShadow: '0 4px 14px rgba(201,168,76,0.15)',
            } : {}}
          >
            <span style={{ fontSize: '1rem' }}>◆</span> La Peschiera
          </button>
          <button
            onClick={() => handleClubChange('jimmys')}
            data-testid="club-tab-jimmys"
            className={`px-4 sm:px-6 py-2.5 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center gap-2 ${
              activeClub === 'jimmys'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={activeClub === 'jimmys' ? {
              background: 'linear-gradient(135deg, rgba(236,72,153,0.3), rgba(168,85,247,0.25))',
              border: '1px solid rgba(236,72,153,0.55)',
              boxShadow: '0 4px 14px rgba(236,72,153,0.2)',
            } : {}}
          >
            <span style={{ fontSize: '1rem' }}>★</span> Jimmy's Club
          </button>
          <button
            onClick={() => handleClubChange('special')}
            data-testid="club-tab-special"
            className={`px-4 sm:px-6 py-2.5 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center gap-2 ${
              activeClub === 'special'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={activeClub === 'special' ? {
              background: 'linear-gradient(135deg, rgba(251,191,36,0.3), rgba(251,146,60,0.22))',
              border: '1px solid rgba(251,191,36,0.5)',
              boxShadow: '0 4px 14px rgba(251,191,36,0.2)',
            } : {}}
          >
            <Anchor size={14} /> Eventi Speciali
          </button>
          <button
            onClick={() => handleClubChange('capodanno')}
            data-testid="club-tab-capodanno"
            className={`px-4 sm:px-6 py-2.5 rounded-xl font-manrope font-700 text-xs sm:text-sm transition-all flex items-center gap-2 ${
              activeClub === 'capodanno'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
            style={activeClub === 'capodanno' ? {
              background: 'linear-gradient(135deg, rgba(251,191,36,0.3), rgba(239,68,68,0.22))',
              border: '1px solid rgba(251,191,36,0.55)',
              boxShadow: '0 4px 14px rgba(251,191,36,0.25)',
            } : {}}
          >
            🎉 Capodanno
          </button>
        </div>

        {/* Special events section — replaces normal tabs when active */}
        {tab !== 'siae' && (
          activeClub === 'special' ? (
          <SpecialEventsSection token={token} />
        ) : (
        <>

        {/* ── DASHBOARD ── */}
        {tab === 'dashboard' && (
          <div data-testid="admin-dashboard-tab">
            <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">
              Dashboard
              <span className="ml-3 text-sm font-manrope font-600 px-3 py-1 rounded-full align-middle" style={
                activeClub === 'jimmys' ? { background: 'rgba(236,72,153,0.15)', color: '#ec4899', border: '1px solid rgba(236,72,153,0.3)' }
                : activeClub === 'capodanno' ? { background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.4)' }
                : { background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }
              } data-testid="dashboard-club-badge">
                {activeClub === 'jimmys' ? "★ Jimmy's Club" : activeClub === 'capodanno' ? '🎉 Capodanno' : '◆ La Peschiera'}
              </span>
            </h1>
            {stats ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
                  <StatCard icon={<Users size={24} />} label="Prenotazioni totali" value={stats.total_bookings} />
                  <StatCard icon={<Calendar size={24} />} label="Serate attive" value={stats.total_events} color="aqua" />
                  <StatCard icon={<BarChart2 size={24} />} label="Persone attese" value={stats.total_people} />
                </div>

                {/* Next Event Section */}
                {stats.next_event && (
                  <div className="mb-10">
                    <h2 className="font-playfair text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Calendar size={18} className="text-gold" />
                      Prossima serata: <span className="text-gold">{stats.next_event.event_title}</span>
                      <span className="text-gray-500 font-manrope text-sm font-normal ml-1">
                        ({new Date(stats.next_event.event_date + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })})
                      </span>
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                      <div className="card-dark p-5" data-testid="next-event-tables-available">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.1)' }}>
                            <Armchair size={20} style={{ color: '#22c55e' }} />
                          </div>
                          <p className="text-gray-400 font-manrope text-sm">Tavoli disponibili</p>
                        </div>
                        <p className="font-playfair text-3xl font-bold" style={{ color: '#22c55e' }}>{stats.next_event.available_tables}</p>
                        <p className="text-gray-600 font-manrope text-xs mt-1">su {stats.next_event.total_tables} totali</p>
                      </div>
                      <div className="card-dark p-5" data-testid="next-event-tables-occupied">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
                            <Armchair size={20} style={{ color: '#ef4444' }} />
                          </div>
                          <p className="text-gray-400 font-manrope text-sm">Tavoli occupati</p>
                        </div>
                        <p className="font-playfair text-3xl font-bold" style={{ color: '#ef4444' }}>{stats.next_event.occupied_tables}</p>
                        <p className="text-gray-600 font-manrope text-xs mt-1">{stats.next_event.total_tables > 0 ? Math.round((stats.next_event.occupied_tables / stats.next_event.total_tables) * 100) : 0}% occupazione</p>
                      </div>
                      <div className="card-dark p-5" data-testid="next-event-revenue">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.1)' }}>
                            <DollarSign size={20} style={{ color: 'var(--color-gold)' }} />
                          </div>
                          <p className="text-gray-400 font-manrope text-sm">Incasso previsto</p>
                        </div>
                        <p className="font-playfair text-3xl font-bold text-gold">&euro; {stats.next_event.expected_revenue.toLocaleString('it-IT')}</p>
                        <p className="text-gray-600 font-manrope text-xs mt-1">{stats.next_event.total_bookings} prenotazioni</p>
                      </div>
                      <div className="card-dark p-5" data-testid="next-event-people">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(79,195,212,0.1)' }}>
                            <Users size={20} style={{ color: 'var(--color-aqua)' }} />
                          </div>
                          <p className="text-gray-400 font-manrope text-sm">Persone attese</p>
                        </div>
                        <p className="font-playfair text-3xl font-bold" style={{ color: 'var(--color-aqua)' }}>{stats.next_event.total_people}</p>
                        <p className="text-gray-600 font-manrope text-xs mt-1">per questa serata</p>
                      </div>
                    </div>

                    {/* Pending table bookings alert */}
                    {stats.next_event?.pending_table_bookings?.length > 0 && (
                      <div className="mt-6 p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }} data-testid="pending-table-alert">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)' }}>
                            <Users size={16} style={{ color: '#ef4444' }} />
                          </div>
                          <p className="font-manrope font-bold text-sm" style={{ color: '#ef4444' }}>
                            {stats.next_event.pending_table_bookings.length} prenotazion{stats.next_event.pending_table_bookings.length === 1 ? 'e' : 'i'} tavolo da assegnare sulla mappa
                          </p>
                        </div>
                        <div className="space-y-2">
                          {stats.next_event.pending_table_bookings.map(b => (
                            <div key={b.id} className="flex items-center justify-between py-1.5 px-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                              <div>
                                <span className="text-white font-manrope font-600 text-sm">{b.first_name} {b.last_name}</span>
                                <span className="text-gray-500 text-xs ml-2">{b.package_label} &bull; {b.num_people} pers.</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-gray-500 font-manrope text-xs mt-3">Vai su <span className="text-gold">Mappa Tavoli</span> per assegnare i tavoli</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick Actions */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8" data-testid="quick-actions">
                  <button
                    onClick={() => setTab('tables')}
                    className="card-dark p-4 flex items-center gap-3 hover:bg-white/5 transition-all group cursor-pointer"
                    data-testid="quick-action-map"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.1)' }}>
                      <MapPin size={18} className="text-gold" />
                    </div>
                    <div>
                      <p className="text-white font-manrope font-600 text-sm group-hover:text-gold transition-colors">Mappa Tavoli</p>
                      <p className="text-gray-600 text-xs">Gestisci tavoli</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setTab('bookings')}
                    className="card-dark p-4 flex items-center gap-3 hover:bg-white/5 transition-all group cursor-pointer"
                    data-testid="quick-action-bookings"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(79,195,212,0.1)' }}>
                      <Users size={18} style={{ color: 'var(--color-aqua)' }} />
                    </div>
                    <div>
                      <p className="text-white font-manrope font-600 text-sm group-hover:text-gold transition-colors">Prenotazioni</p>
                      <p className="text-gray-600 text-xs">Vedi tutte</p>
                    </div>
                  </button>
                  {stats.next_event && (
                    <button
                      onClick={() => { const ne = events.find(e => e.title === stats.next_event.event_title); if (ne) exportEventPDF(ne); }}
                      className="card-dark p-4 flex items-center gap-3 hover:bg-white/5 transition-all group cursor-pointer"
                      data-testid="quick-action-pdf"
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.1)' }}>
                        <Download size={18} style={{ color: '#22c55e' }} />
                      </div>
                      <div>
                        <p className="text-white font-manrope font-600 text-sm group-hover:text-gold transition-colors">PDF Presenze</p>
                        <p className="text-gray-600 text-xs">Prossima serata</p>
                      </div>
                    </button>
                  )}
                  {stats.next_event && (
                    <button
                      onClick={() => { const ne = events.find(e => e.title === stats.next_event.event_title); if (ne) exportEventTablesPDF(ne); }}
                      className="card-dark p-4 flex items-center gap-3 hover:bg-white/5 transition-all group cursor-pointer"
                      data-testid="quick-action-pdf-tables"
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.1)' }}>
                        <Armchair size={18} className="text-gold" />
                      </div>
                      <div>
                        <p className="text-white font-manrope font-600 text-sm group-hover:text-gold transition-colors">PDF Tavoli</p>
                        <p className="text-gray-600 text-xs">Prossima serata</p>
                      </div>
                    </button>
                  )}
                  <button
                    onClick={() => setTab('events')}
                    className="card-dark p-4 flex items-center gap-3 hover:bg-white/5 transition-all group cursor-pointer"
                    data-testid="quick-action-events"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.1)' }}>
                      <Calendar size={18} style={{ color: '#a855f7' }} />
                    </div>
                    <div>
                      <p className="text-white font-manrope font-600 text-sm group-hover:text-gold transition-colors">Gestione Eventi</p>
                      <p className="text-gray-600 text-xs">Crea e modifica</p>
                    </div>
                  </button>
                </div>

                {/* Per event + Best Package + Latest bookings */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                  {/* Prenotazioni per serata */}
                  <div className="card-dark p-6">
                    <h3 className="font-playfair text-lg font-bold text-white mb-4">Prenotazioni per serata</h3>
                    <div className="space-y-3">
                      {stats.per_event.length === 0 && <p className="text-gray-400 text-sm">Nessuna prenotazione ancora.</p>}
                      {stats.per_event.map((e, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b border-white/5">
                          <span className="text-gray-300 font-manrope text-sm truncate max-w-48">{e.event}</span>
                          <div className="flex gap-3 shrink-0">
                            <span className="text-gold font-600 text-sm">{e.bookings} pren.</span>
                            <span className="text-aqua font-600 text-sm">{e.people} pers.</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pacchetto più venduto - Prominent */}
                  <div className="card-dark p-6" data-testid="best-package">
                    <div className="flex items-center gap-2 mb-5">
                      <Trophy size={18} className="text-gold" />
                      <h3 className="font-playfair text-lg font-bold text-white">Pacchetto Top</h3>
                    </div>
                    {stats.per_package.length > 0 ? (
                      <>
                        <div className="text-center mb-5 py-4 rounded-xl" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                          <p className="font-playfair text-2xl font-bold text-gold mb-1">{stats.per_package[0].package}</p>
                          <p className="text-gray-400 font-manrope text-sm">{stats.per_package[0].count} prenotazioni</p>
                        </div>
                        {stats.per_package.length > 1 && (
                          <div className="space-y-2">
                            {stats.per_package.slice(1).map((p, i) => {
                              const maxCount = stats.per_package[0].count;
                              const pct = maxCount > 0 ? Math.round((p.count / maxCount) * 100) : 0;
                              return (
                                <div key={i}>
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-gray-400 font-manrope text-xs truncate max-w-36">{p.package}</span>
                                    <span className="text-gray-300 font-manrope text-xs font-600">{p.count}</span>
                                  </div>
                                  <div className="w-full h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
                                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'var(--color-gold)', opacity: 0.6 }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-400 text-sm">Nessuna prenotazione ancora.</p>
                    )}
                  </div>

                  {/* Ultime prenotazioni in tempo reale */}
                  <div className="card-dark p-6" data-testid="latest-bookings">
                    <div className="flex items-center gap-2 mb-5">
                      <Clock size={18} style={{ color: 'var(--color-aqua)' }} />
                      <h3 className="font-playfair text-lg font-bold text-white">Ultime prenotazioni</h3>
                    </div>
                    {stats.latest_bookings && stats.latest_bookings.length > 0 ? (
                      <div className="space-y-3">
                        {stats.latest_bookings.map((b, i) => {
                          const timeAgo = (() => {
                            const diff = Date.now() - new Date(b.created_at).getTime();
                            const mins = Math.floor(diff / 60000);
                            if (mins < 1) return 'ora';
                            if (mins < 60) return `${mins} min fa`;
                            const hrs = Math.floor(mins / 60);
                            if (hrs < 24) return `${hrs}h fa`;
                            const days = Math.floor(hrs / 24);
                            return `${days}g fa`;
                          })();
                          return (
                            <div key={b.id || i} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: i === 0 ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.04)' }}>
                                <span className="font-manrope font-bold text-xs" style={{ color: i === 0 ? 'var(--color-gold)' : '#666' }}>
                                  {b.first_name?.[0]}{b.last_name?.[0]}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-manrope font-600 text-sm">{b.first_name} {b.last_name}</p>
                                <p className="text-gray-500 text-xs truncate">{b.event_title} &bull; {b.num_people} pers.</p>
                              </div>
                              <span className="text-gray-600 font-manrope text-xs shrink-0">{timeAgo}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">Nessuna prenotazione ancora.</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-400">Caricamento statistiche...</p>
            )}
          </div>
        )}

        {/* ── BOOKINGS ── */}
        {tab === 'bookings' && (
          <div data-testid="admin-bookings-tab">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-white">Prenotazioni</h1>
              <select
                value={filterEvent}
                onChange={e => setFilterEvent(e.target.value)}
                className="input-elegant w-full sm:w-auto"
                style={{ minWidth: '200px' }}
              >
                <option value="">Tutte le serate</option>
                {events.map(ev => (
                  <option key={ev.id} value={ev.id}>{ev.title} ({ev.date})</option>
                ))}
              </select>
            </div>

            {bookings.length === 0 ? (
              <div className="card-dark p-12 text-center">
                <p className="text-gray-400 font-manrope">Nessuna prenotazione trovata.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Group bookings by event */}
                {(() => {
                  const groups = {};
                  bookings.forEach(b => {
                    const key = b.event_id || 'unknown';
                    if (!groups[key]) groups[key] = { title: b.event_title, date: b.event_date, bookings: [] };
                    groups[key].bookings.push(b);
                  });
                  return Object.entries(groups).map(([eventId, group]) => (
                    <div key={eventId}>
                      <div className="flex items-center gap-3 mb-3 pb-2 border-b border-white/10">
                        <Calendar size={16} className="text-gold shrink-0" />
                        <h2 className="font-playfair text-lg font-bold text-gold">{group.title}</h2>
                        <span className="text-gray-500 font-manrope text-sm">
                          {group.date && new Date((group.date || '').split(' ')[0].split('T')[0] + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span>
                        <span className="ml-auto text-gray-400 font-manrope text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(201,168,76,0.1)' }}>
                          {group.bookings.length} prenotazion{group.bookings.length === 1 ? 'e' : 'i'} &bull; {group.bookings.reduce((s, b) => s + (b.num_people || 0), 0)} pers.
                        </span>
                      </div>
                      <div className="space-y-2">
                        {group.bookings.map(b => (
                          <div key={b.id} data-testid={`booking-row-${b.id}`} className="card-dark overflow-hidden">
                            <div
                              className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/3 transition-colors"
                              onClick={() => setExpandedBooking(expandedBooking === b.id ? null : b.id)}
                            >
                              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                                <span className="font-playfair font-bold text-gold text-xs sm:text-sm shrink-0">{b.booking_code}</span>
                                <span className="text-white font-manrope font-600 text-sm truncate">{b.first_name} {b.last_name}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full font-manrope font-600 shrink-0" style={{
                                  background: b.package_type?.includes('tavolo') ? 'rgba(201,168,76,0.12)' : 'rgba(79,195,212,0.12)',
                                  color: b.package_type?.includes('tavolo') ? '#c9a84c' : '#4fc3d4',
                                }}>
                                  {b.package_type?.includes('tavolo') ? (b.table_number ? `Tav. ${b.table_number}` : 'Tavolo') : 'Ingresso'}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-gray-500 text-sm hidden sm:block">{b.num_people} pers.</span>
                                <button
                                  onClick={e => { e.stopPropagation(); handleDeleteBooking(b.id); }}
                                  className="text-gray-600 hover:text-red-400 transition-colors p-1"
                                  title="Elimina"
                                >
                                  <Trash2 size={16} />
                                </button>
                                {expandedBooking === b.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                              </div>
                            </div>
                            {expandedBooking === b.id && (
                              <div className="px-4 pb-4 border-t border-white/5 pt-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                  {[
                                    ['Pacchetto', b.package_label],
                                    ['Persone', b.num_people],
                                    ['Telefono', b.phone || '—'],
                                    ['Email', b.email || '—'],
                                    ['Posizione', b.table_position || '—'],
                                    ['Tavolo', b.table_number || '—'],
                                    ['Prenotato il', new Date(b.created_at).toLocaleDateString('it-IT')],
                                    ['Codice', b.booking_code],
                                  ].map(([k, v]) => (
                                    <div key={k}>
                                      <p className="text-gray-500 text-xs font-manrope uppercase tracking-wider mb-0.5">{k}</p>
                                      <p className="text-white font-manrope text-sm">{v}</p>
                                    </div>
                                  ))}
                                </div>
                                {b.extras && (b.extras.torta || b.extras.bottiglia || b.extras.apericena) && (
                                  <div className="flex flex-wrap gap-2 mb-3">
                                    {b.extras.torta && <span className="text-xs px-2.5 py-1 rounded-full font-manrope" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>🎂 Torta{b.extras.torta_testo ? `: "${b.extras.torta_testo}"` : ''}</span>}
                                    {b.extras.bottiglia && <span className="text-xs px-2.5 py-1 rounded-full font-manrope" style={{ background: 'rgba(168,139,250,0.1)', color: '#a78bfa' }}>🍾 {b.extras.bottiglia_nome || 'Bottiglia'}</span>}
                                    {b.extras.apericena && <span className="text-xs px-2.5 py-1 rounded-full font-manrope" style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c' }}>🍽 Apericena</span>}
                                  </div>
                                )}
                                {b.notes && <p className="text-gray-400 text-xs italic mb-3">Note: {b.notes}</p>}
                                {b.package_type?.includes('tavolo') && (
                                  <TableAssignField booking={b} token={token} onSaved={loadBookings} />
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        )}

        {/* ── EVENTS MANAGEMENT ── */}
        {tab === 'events' && (
          <div data-testid="admin-events-tab">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-playfair text-3xl font-bold text-white">Gestione eventi</h1>
              <button
                data-testid="add-event-btn"
                onClick={() => { setEditEvent(null); setShowModal(true); }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={18} /> Nuovo evento
              </button>
            </div>
            <div className="space-y-3">
              {events.map(ev => (
                <div key={ev.id} data-testid={`event-row-${ev.id}`} className="card-dark p-5 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-playfair font-bold text-white text-lg leading-tight">{ev.title}</p>
                      <span className="text-xs font-manrope font-700 px-2 py-0.5 rounded-full" data-testid={`event-club-badge-${ev.id}`} style={
                        ev.club === 'jimmys' ? { background: 'rgba(236,72,153,0.15)', color: '#ec4899', border: '1px solid rgba(236,72,153,0.3)' }
                        : ev.club === 'capodanno' ? { background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.4)' }
                        : { background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }
                      }>
                        {ev.club === 'jimmys' ? "★ Jimmy's" : ev.club === 'capodanno' ? '🎉 Capodanno' : '◆ Peschiera'}
                      </span>
                    </div>
                    <p className="text-gold font-manrope text-sm mt-1">{ev.date} &bull; ore {ev.time}</p>
                    <p className="text-gray-400 text-sm mt-0.5 truncate">{ev.description}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`export-event-pdf-${ev.id}`}
                      onClick={() => exportEventPDF(ev)}
                      className="p-2 rounded-lg text-gray-400 hover:text-gold hover:bg-white/5 transition-colors"
                      title="Esporta PDF presenze"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      data-testid={`edit-event-${ev.id}`}
                      onClick={() => { setEditEvent(ev); setShowModal(true); }}
                      className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-gray-400 hover:border-gold hover:text-gold transition-all"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      data-testid={`delete-event-${ev.id}`}
                      onClick={() => handleDeleteEvent(ev.id)}
                      className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-gray-400 hover:border-red-400 hover:text-red-400 transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {events.length === 0 && (
                <div className="card-dark p-12 text-center">
                  <p className="text-gray-400 font-manrope">Nessun evento attivo.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TABLE MAP ── */}
        {tab === 'tables' && (
          <TableMap events={events} token={token} />
        )}
        </>
        ))}

        {/* ── SIAE (trasversale, non filtrato per club) ── */}
        {tab === 'siae' && (
          <SiaeSection token={token} />
        )}
      </main>

      {showModal && (
        <EventModal
          event={editEvent}
          token={token}
          defaultClub={activeClub}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadEvents(); loadStats(); }}
        />
      )}
    </div>
  );
}
